import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  ArrowRight,
  ChevronDown,
  Palette,
  Store,
  Building2,
  MessageSquare,
  Compass,
  CreditCard,
  Layers,
  Sparkles,
  Info
} from 'lucide-react';

import subscriptionPlansMockup from '../../assets/Update_Images/Subscription_Page_img.png';
import voucherTemplateImg from '../../assets/Update_Images/Voucher_template_img.png';
import partnerPageImg from '../../assets/Update_Images/Partner_Page_img.png';
import travellerChatImg from '../../assets/Update_Images/Traveller_Chat_img.png';
import diningAppImg from '../../assets/App_Img/Dinning_page.png';
import dashboardPageImg from '../../assets/Update_Images/Dashboard_Page_img.png';

const FEATURES = [
  {
    id: 'dashboard-voucher-creation',
    title: 'Create Vouchers Directly from Dashboard',
    icon: Sparkles,
    route: '/dashboard',
    buttonText: 'Try Dashboard Voucher Creator',
    image: dashboardPageImg,
    imageType: 'desktop',
    shortSummary: 'Create branded travel vouchers instantly directly from your main dashboard overview.',
    description: 'Create vouchers effortlessly without leaving your dashboard. Simply drag and drop hotel confirmations, flight tickets, or itineraries into the quick upload zones. Trrip Magic automatically extracts journey details, guests, and builds your branded voucher in seconds.',
    highlights: [
      'Instant drag & drop zones for Hotel, Travel & Itinerary files',
      'Automatic extraction of guest names, dates & destinations',
      'One-click handoff to interactive voucher editor or instant generation',
      'Unified quick-action workflow without leaving the dashboard'
    ]
  },
  {
    id: 'live-chat',
    title: 'Live Traveller & Agent Chat',
    icon: MessageSquare,
    route: '/support',
    buttonText: 'Open Chat Console',
    image: travellerChatImg,
    imageType: 'desktop',
    shortSummary: 'Direct real-time two-way messaging between your operations team and travellers on tour.',
    description: 'Deliver instant concierge-level service during the trip. Travellers can message your operations team directly from their mobile app, while your team replies in real time with live socket connectivity, attachments, and unread badges.',
    highlights: [
      'Direct two-way in-app messaging connected to booking IDs',
      'Real-time socket updates and instant unread count badges',
      'Fast image & document attachment sharing',
      'Seamless communication without leaving your dashboard'
    ]
  },
  {
    id: 'voucher-template',
    title: 'Custom Voucher Template',
    icon: Palette,
    route: '/voucher-template',
    buttonText: 'Customize Voucher Template',
    image: voucherTemplateImg,
    imageType: 'desktop',
    shortSummary: 'Tailor your vouchers with your agency branding, custom color palettes, and header styling.',
    description: 'Elevate your brand presence across every customer touchpoint. Personalize your voucher layouts, inject your agency hex codes, and present travellers with professional, high-standard itineraries.',
    highlights: [
      'Agency color palette and custom branding accent',
      'Dynamic typography and high-definition header presets',
      'Live synchronized real-time voucher preview'
    ]
  },
  {
    id: 'partner-management',
    title: 'Add & Manage Your Own Vendors',
    icon: Building2,
    route: '/partners',
    buttonText: 'Manage Partners & Vendors',
    image: partnerPageImg,
    imageType: 'desktop',
    shortSummary: 'Directly onboard your preferred hotels, restaurants, activity providers, and local shops into your workspace.',
    description: 'You are no longer restricted to platform partners. Directly register your trusted ground suppliers across 107+ destinations. Manage contact details, category tags, address coordinates, and active status in one centralized directory.',
    highlights: [
      'Add custom Dining, Activity & Shopping partners',
      'Direct contact numbers & destination mapping',
      'One-click freeze/unfreeze status controls'
    ]
  },
  {
    id: 'app-vendors',
    title: 'Showcase Your Vendors in Traveller App',
    icon: Store,
    image: diningAppImg,
    imageType: 'mobile',
    shortSummary: 'Seamlessly display your verified dining, shopping, and activity partners inside your travellers\' mobile application.',
    description: 'Delight your clients on tour with handpicked recommendations. The \'Show My Partners\' toggle on voucher creation allows your private partners to be showcased prominently in the traveller app\'s Dining, Activities, and Shopping tabs.',
    highlights: [
      'Simple toggle control under Voucher Partner Visibility',
      'Showcase preferred partner menus, photos & locations',
      'Ensure travellers visit your trusted local spots',
      'Exclusive to Growth & Enterprise plan agencies'
    ],
    infoNote: "You'll find this option directly inside the tour voucher creation form under 'Partner Visibility' — simply toggle 'Show My Partners' when generating a voucher."
  }
];

const FeatureUpdateModal = ({
  isProfilingOpen,
  forceOpen,
  onClose,
  isOpen: controlledIsOpen,
  initialSlide = 0
}) => {
  const navigate = useNavigate();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const [expandedFeatureIndex, setExpandedFeatureIndex] = useState(null);

  useEffect(() => {
    if (controlledIsOpen !== undefined) {
      return;
    }
    if (forceOpen) {
      setInternalIsOpen(true);
      return;
    }
    const hasSeenUpdate = localStorage.getItem('hasSeenUpdate_v4');
    if (isProfilingOpen) {
      setInternalIsOpen(false);
    } else if (!hasSeenUpdate) {
      const timer = setTimeout(() => {
        setInternalIsOpen(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [isProfilingOpen, forceOpen, controlledIsOpen]);

  useEffect(() => {
    if (initialSlide !== undefined) {
      setCurrentSlide(initialSlide);
    }
    if (isOpen) {
      setExpandedFeatureIndex(null);
    }
  }, [initialSlide, isOpen]);

  const handleClose = () => {
    setInternalIsOpen(false);
    localStorage.setItem('hasSeenUpdate_v4', 'true');
    if (onClose) onClose();
  };

  const handleNavigate = (route) => {
    handleClose();
    if (route) {
      navigate(route);
    }
  };

  const toggleFeature = (index) => {
    setExpandedFeatureIndex((prev) => (prev === index ? null : index));
  };

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-2.5 sm:p-4 md:p-6 bg-slate-950/70 backdrop-blur-xs overflow-y-auto">
          {/* Main Modal Shell */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 10 }}
            transition={{ type: 'spring', stiffness: 360, damping: 30 }}
            className="bg-white rounded-2xl sm:rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] w-full max-w-5xl h-[92vh] sm:h-[730px] md:h-[760px] max-h-[94vh] overflow-hidden relative flex flex-col border border-slate-100 my-auto"
          >
            {/* Top Navigation Bar: Clean, Responsive Tabs */}
            <div className="px-3 sm:px-6 py-2 sm:py-2.5 border-b border-slate-100 flex items-center justify-between bg-white/95 backdrop-blur-xs shrink-0 z-20">
              <div className="flex items-center bg-slate-100/90 p-1 rounded-xl flex-1 sm:flex-initial max-w-sm sm:max-w-md min-w-0 mr-2">
                <button
                  type="button"
                  onClick={() => setCurrentSlide(0)}
                  className={`flex-1 sm:flex-initial px-2.5 sm:px-4 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1.5 cursor-pointer min-w-0 ${currentSlide === 0
                    ? 'bg-white text-[#067978] shadow-xs font-semibold'
                    : 'text-slate-600 hover:text-slate-900'
                    }`}
                >
                  <Layers size={14} className="shrink-0 text-[#067978]" />
                  <span className="truncate">New Features</span>
                </button>

                <button
                  type="button"
                  onClick={() => setCurrentSlide(1)}
                  className={`flex-1 sm:flex-initial px-2.5 sm:px-4 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1.5 cursor-pointer min-w-0 ${currentSlide === 1
                    ? 'bg-white text-[#067978] shadow-xs font-semibold'
                    : 'text-slate-600 hover:text-slate-900'
                    }`}
                >
                  <CreditCard size={14} className="shrink-0 text-[#067978]" />
                  <span className="truncate">Subscriptions & Plan</span>
                </button>
              </div>

              {/* Close Button */}
              <button
                type="button"
                onClick={handleClose}
                className="p-1.5 sm:p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors cursor-pointer shrink-0"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Slide Body */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              <AnimatePresence mode="wait">
                {currentSlide === 0 ? (
                  /* SLIDE 1: INTERACTIVE NEW FEATURES DROPDOWN ACCORDION */
                  <motion.div
                    key="slide-features"
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{ duration: 0.2 }}
                    className="p-3.5 sm:p-6 md:p-7 space-y-4"
                  >
                    {/* Header */}
                    <div className="text-center max-w-xl mx-auto space-y-1 mb-2">
                      <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight">
                        Powerful New Features Are Live Now!
                      </h2>
                      <p className="text-xs sm:text-sm text-slate-500 font-normal">
                        Click on any feature below to expand its preview, discover capabilities, and jump right into the tool.
                      </p>
                    </div>

                    {/* Accordion Feature List */}
                    <div className="space-y-3 pt-1">
                      {FEATURES.map((feature, index) => {
                        const isExpanded = expandedFeatureIndex === index;
                        const IconComponent = feature.icon;

                        return (
                          <div
                            key={feature.id}
                            className={`rounded-2xl border transition-all duration-200 overflow-hidden ${isExpanded
                              ? 'border-[#067978]/60 bg-white shadow-sm ring-1 ring-[#067978]/20'
                              : 'border-slate-200 bg-slate-50/60 hover:bg-slate-50 hover:border-slate-300'
                              }`}
                          >
                            {/* Accordion Header / Trigger */}
                            <button
                              type="button"
                              onClick={() => toggleFeature(index)}
                              className="w-full p-3.5 sm:p-4 flex items-center justify-between text-left transition-colors cursor-pointer gap-3"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 bg-[#067978]/10 text-[#067978]">
                                  <IconComponent size={18} />
                                </div>
                                <div className="min-w-0">
                                  <h3 className="text-xs sm:text-sm font-semibold text-slate-900 truncate">
                                    {feature.title}
                                  </h3>
                                  <p className="text-[11px] sm:text-xs text-slate-500 font-normal truncate mt-0.5">
                                    {feature.shortSummary}
                                  </p>
                                </div>
                              </div>

                              <div className="shrink-0 flex items-center gap-2">
                                <span className="text-[11px] font-medium text-[#067978] hidden sm:inline-block">
                                  {isExpanded ? 'Hide Details' : 'View Preview'}
                                </span>
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-transform duration-200 ${isExpanded ? 'rotate-180 bg-[#067978] text-white' : 'bg-slate-200/70 text-slate-600'
                                  }`}>
                                  <ChevronDown size={15} />
                                </div>
                              </div>
                            </button>

                            {/* Accordion Expandable Content */}
                            <AnimatePresence initial={false}>
                              {isExpanded && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.24, ease: 'easeInOut' }}
                                  className="overflow-hidden border-t border-slate-100 bg-white"
                                >
                                  <div className="p-4 sm:p-5 md:p-6 grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 items-center">
                                    {/* Left Column: Visual Mockup Showcase */}
                                    <div className="md:col-span-6 flex justify-center">
                                      {feature.imageType === 'mobile' ? (
                                        /* Sleek Ultra-Thin Minimalist Mobile Phone Frame (Matching Sample Voucher Tab) */
                                        <div className="relative flex items-center justify-center">
                                          <div className="w-[185px] xs:w-[195px] sm:w-[205px] h-[370px] xs:h-[390px] sm:h-[410px] bg-slate-900 rounded-[28px] p-[2.5px] shadow-lg border border-slate-700/60 relative overflow-hidden group shrink-0">
                                            <div className="rounded-[25px] overflow-hidden bg-slate-950 w-full h-full relative">
                                              <img
                                                src={feature.image}
                                                alt={feature.title}
                                                className="w-full h-full object-cover object-top"
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        /* Desktop / Tablet Browser Showcase */
                                        <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-md overflow-hidden flex flex-col">
                                          <div className="bg-slate-100 px-3 py-1.5 border-b border-slate-200 flex items-center justify-between">
                                            <div className="flex items-center space-x-1.5">
                                              <span className="w-2.5 h-2.5 rounded-full bg-rose-400"></span>
                                              <span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
                                              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
                                            </div>
                                            <span className="text-[9.5px] font-mono text-slate-500">
                                              trrip.live{feature.route}
                                            </span>
                                            <span className="w-6"></span>
                                          </div>
                                          <div className="p-2 sm:p-2.5 bg-slate-50 flex items-center justify-center min-h-[190px] sm:min-h-[220px]">
                                            <img
                                              src={feature.image}
                                              alt={feature.title}
                                              className="max-h-[240px] sm:max-h-[260px] w-full object-contain rounded-lg shadow-2xs"
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    {/* Right Column: Descriptions & Action */}
                                    <div className="md:col-span-6 space-y-3.5 flex flex-col justify-center">
                                      <div>
                                        <h4 className="text-sm sm:text-base font-semibold text-slate-900 leading-snug">
                                          {feature.title}
                                        </h4>
                                        <p className="text-xs text-slate-600 font-normal leading-relaxed mt-1.5">
                                          {feature.description}
                                        </p>
                                      </div>

                                      {/* Highlights bullet list */}
                                      <div className="space-y-1.5 bg-slate-50 p-3 rounded-xl border border-slate-200">
                                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">
                                          Key Highlights
                                        </span>
                                        {feature.highlights.map((h, i) => (
                                          <div key={i} className="flex items-start gap-2 text-xs text-slate-700 font-normal">
                                            <CheckCircle2 size={13} className="text-[#067978] shrink-0 mt-0.5" />
                                            <span className="leading-snug">{h}</span>
                                          </div>
                                        ))}
                                      </div>

                                      {/* Action Button or Info Callout */}
                                      <div className="pt-1">
                                        {feature.buttonText && feature.route ? (
                                          <button
                                            type="button"
                                            onClick={() => handleNavigate(feature.route)}
                                            className="w-full sm:w-auto px-5 py-2.5 bg-[#067978] hover:bg-[#056362] text-white rounded-xl text-xs sm:text-sm font-medium transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2 cursor-pointer active:scale-98"
                                          >
                                            <span>{feature.buttonText}</span>
                                            <ArrowRight size={14} />
                                          </button>
                                        ) : (
                                          <div className="flex items-start gap-2.5 text-xs text-slate-600 bg-slate-50/80 rounded-xl p-3 border border-slate-200/80">
                                            <Info size={15} className="text-[#067978] shrink-0 mt-0.5" />
                                            <span className="leading-relaxed">
                                              {feature.infoNote || "You'll find this option directly inside the tour voucher creation form under 'Partner Visibility'."}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                ) : (
                  /* SLIDE 2: SUBSCRIPTION PLANS SHOWCASE (IMAGE PREVIEW & INFORMATION) */
                  <motion.div
                    key="slide-subscriptions"
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 12 }}
                    transition={{ duration: 0.2 }}
                    className="p-3.5 sm:p-6 md:p-7 space-y-4 sm:space-y-5"
                  >
                    {/* Header */}
                    <div className="text-center max-w-xl mx-auto space-y-1">
                      <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight">
                        Subscription Plans Are Live!
                      </h2>
                      <p className="text-xs sm:text-sm text-slate-500 font-normal">
                        Choose the right plan that fits your business scale. Enjoy transparent 30-day billing, instant voucher quotas, and premium travel tools.
                      </p>
                    </div>

                    {/* Content Grid: Mockup Image Preview on Left, Information & Action on Right */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 items-center pt-2">
                      {/* Left: Desktop Browser Mockup Frame */}
                      <div className="lg:col-span-7 flex justify-center">
                        <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-md overflow-hidden flex flex-col">
                          <div className="bg-slate-100 px-3.5 py-2 border-b border-slate-200 flex items-center justify-between">
                            <div className="flex items-center space-x-1.5">
                              <span className="w-2.5 h-2.5 rounded-full bg-rose-400"></span>
                              <span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
                              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
                            </div>
                            <span className="text-[10px] sm:text-xs font-mono text-slate-500">
                              trrip.live/subscription
                            </span>
                            <span className="w-8"></span>
                          </div>
                          <div className="p-2 sm:p-3 bg-slate-50 flex items-center justify-center">
                            <img
                              src={subscriptionPlansMockup}
                              alt="Trrip Subscription Plans"
                              className="w-full h-auto max-h-[300px] sm:max-h-[350px] object-contain rounded-xl shadow-xs"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Right: Informational Copy & CTA Button */}
                      <div className="lg:col-span-5 space-y-3.5 flex flex-col justify-center">
                        <div>
                          <h3 className="text-base sm:text-lg font-semibold text-slate-900 leading-snug">
                            Tailored Plans for Every Travel Agency
                          </h3>
                          <p className="text-xs text-slate-600 font-normal leading-relaxed mt-1.5">
                            Whether you are just getting started or managing hundreds of departures every month, our transparent subscription tiers empower your operations with branded apps, live location, and custom templates.
                          </p>
                        </div>

                        {/* Feature Highlights Card */}
                        <div className="space-y-2.5 bg-gradient-to-b from-white to-slate-50/80 p-3.5 sm:p-4 rounded-2xl border border-slate-200/70 shadow-2xs">
                          <div className="flex items-start gap-3 text-xs text-slate-700">
                            <div className="w-5 h-5 rounded-lg bg-teal-50 border border-teal-100/80 flex items-center justify-center shrink-0 mt-0.5 text-[#067978]">
                              <CheckCircle2 size={13} className="stroke-[2.5]" />
                            </div>
                            <div className="leading-relaxed">
                              <span className="font-semibold text-slate-900">4 Flexible Tiers:</span> Starter, Pro, Growth, & custom Enterprise.
                            </div>
                          </div>

                          <div className="flex items-start gap-3 text-xs text-slate-700">
                            <div className="w-5 h-5 rounded-lg bg-teal-50 border border-teal-100/80 flex items-center justify-center shrink-0 mt-0.5 text-[#067978]">
                              <CheckCircle2 size={13} className="stroke-[2.5]" />
                            </div>
                            <div className="leading-relaxed">
                              <span className="font-semibold text-slate-900">Show Your Vendors:</span> Showcase your trusted ground partners and private suppliers directly in the traveller mobile app.
                            </div>
                          </div>

                          <div className="flex items-start gap-3 text-xs text-slate-700">
                            <div className="w-5 h-5 rounded-lg bg-teal-50 border border-teal-100/80 flex items-center justify-center shrink-0 mt-0.5 text-[#067978]">
                              <CheckCircle2 size={13} className="stroke-[2.5]" />
                            </div>
                            <div className="leading-relaxed">
                              <span className="font-semibold text-slate-900">Earn More Commission:</span> Unlock higher commission rates, preferential vendor margins, and boosted revenue across plans.
                            </div>
                          </div>

                          <div className="flex items-start gap-3 text-xs text-slate-700">
                            <div className="w-5 h-5 rounded-lg bg-teal-50 border border-teal-100/80 flex items-center justify-center shrink-0 mt-0.5 text-[#067978]">
                              <CheckCircle2 size={13} className="stroke-[2.5]" />
                            </div>
                            <div className="leading-relaxed">
                              <span className="font-semibold text-slate-900">Exclusive Premium Features:</span> Unlock custom PDF voucher templates, partner networks, traveller chat, and more.
                            </div>
                          </div>
                        </div>

                        {/* Direct Redirect Button */}
                        <div className="pt-1">
                          <button
                            type="button"
                            onClick={() => handleNavigate('/subscription')}
                            className="w-full sm:w-auto px-6 py-2.5 sm:py-3 bg-[#067978] hover:bg-[#056362] text-white rounded-xl text-xs sm:text-sm font-semibold transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2 cursor-pointer active:scale-98"
                          >
                            <span>Explore Plans & Pricing</span>
                            <ArrowRight size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Bottom Controls Footer */}
            <div className="px-3 sm:px-6 py-2.5 sm:py-3 border-t border-slate-100 bg-white flex items-center justify-between shrink-0 gap-2 z-20">
              {/* Slide pagination dots */}
              <div className="flex items-center space-x-1.5 shrink-0">
                {[0, 1].map((idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setCurrentSlide(idx)}
                    className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${currentSlide === idx ? 'w-5 sm:w-6 bg-[#067978]' : 'w-2 bg-slate-200 hover:bg-slate-300'
                      }`}
                    title={`Slide ${idx + 1}`}
                  />
                ))}
                <span className="text-[10.5px] sm:text-xs text-slate-400 font-normal ml-1 hidden xs:inline-block">
                  {currentSlide + 1} of 2
                </span>
              </div>

              {/* Navigation Action Buttons */}
              <div className="flex items-center space-x-1.5 sm:space-x-2 shrink-0">
                {currentSlide === 0 ? (
                  <button
                    type="button"
                    onClick={() => setCurrentSlide(1)}
                    className="px-3 sm:px-5 py-1.5 sm:py-2 bg-[#067978] hover:bg-[#056362] text-white rounded-xl text-xs sm:text-sm font-medium transition-all shadow-sm hover:shadow-md flex items-center gap-1 cursor-pointer active:scale-95"
                  >
                    <span className="hidden sm:inline">Next: Subscriptions & Plan</span>
                    <span className="sm:hidden">Next</span>
                    <ChevronRight size={14} />
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setCurrentSlide(0)}
                      className="px-2.5 sm:px-4 py-1.5 sm:py-2 text-slate-600 hover:text-[#067978] hover:bg-teal-50/80 rounded-xl text-xs sm:text-sm font-medium transition-all flex items-center gap-1 border border-slate-200 cursor-pointer"
                    >
                      <ChevronLeft size={14} />
                      <span className="hidden sm:inline">Back to Features</span>
                      <span className="sm:hidden">Back</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-3 sm:px-5 py-1.5 sm:py-2 bg-[#067978] hover:bg-[#056362] text-white rounded-xl text-xs sm:text-sm font-medium transition-all shadow-sm hover:shadow-md flex items-center gap-1 cursor-pointer active:scale-95"
                    >
                      <span>Got It! Close</span>
                      <CheckCircle2 size={14} />
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null;
};

export default FeatureUpdateModal;
